import posthog from "posthog-js";
import { getLandingTrackingContext } from "./landing-experiment";

let currentBid: string | undefined;
let isInitialized = false;

// Events fired before PostHog finished initializing (init is deferred until after
// first paint) are buffered here and flushed once the client is ready.
const pendingEvents: Array<{
  eventName: string;
  properties?: Record<string, unknown>;
}> = [];

export function setPostHogBid(bid: string) {
  currentBid = bid;
}

const runAfterFirstPaint = (fn: () => void): void => {
  const idle = (
    window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => void;
    }
  ).requestIdleCallback;

  requestAnimationFrame(() => {
    if (idle) idle(fn, { timeout: 2000 });
    else setTimeout(fn, 0);
  });
};

/**
 * Initializes PostHog on the client only, after the first paint, so analytics never
 * blocks the landing page's first render. Safe to call more than once.
 */
export function initPostHog(): void {
  if (import.meta.env.SSR || isInitialized) return;

  const token: string | undefined = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  if (!token) return;

  runAfterFirstPaint(() => {
    if (isInitialized) return;

    try {
      posthog.init(token, {
        api_host: "https://us.i.posthog.com",
        defaults: "2026-01-30",
        capture_exceptions: true,
        // Emits $web_vitals events (LCP / CLS / FCP / INP) so page performance is measurable.
        capture_performance: { web_vitals: true },
        // Enrich SDK autocapture and legacy direct captures on landing routes too.
        // Custom events already carry occurrence-time context, which wins here.
        before_send: (event) =>
          event
            ? {
                ...event,
                properties: {
                  ...getLandingTrackingContext(),
                  ...event.properties,
                },
              }
            : null,
      });
      isInitialized = true;

      while (pendingEvents.length) {
        const queued = pendingEvents.shift();
        if (queued) posthog.capture(queued.eventName, queued.properties);
      }
    } catch (e) {
      console.error("Failed to load posthog:", e);
    }
  });
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
) {
  if (import.meta.env.SSR) return;

  properties = { ...getLandingTrackingContext(), ...properties };

  // Queue anything captured before init resolves so no event is silently dropped.
  if (!isInitialized) {
    pendingEvents.push({ eventName, properties });
    return;
  }

  try {
    posthog.capture(eventName, {
      ...properties,
      ...(currentBid ? { bid: currentBid } : {}),
    });
  } catch (e) {
    console.error("PostHog tracking error:", e);
  }
}

export function identifyBusiness(
  bid: string,
  properties?: Record<string, unknown>
) {
  try {
    posthog.group("business", bid, properties);
  } catch (e) {
    console.error("PostHog group error:", e);
  }
}
